import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import {
  getTokenExpiration,
  validateRefreshToken,
  isTokenExpired,
} from "../utils/auth";
import { AuthLogger } from "../utils/authLogger";
import Intercom from "@intercom/messenger-js-sdk";
import Cookies from "js-cookie";
import { when } from "../hooks/useWhen";
import { END_OF_PILOT_25_ARGS } from "../consts/feature-config";

const AuthContext = createContext();

const { REACT_APP_API } = process.env;

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Error parsing stored user:", e);
      return null;
    }
  });
  const [authTokens, setAuthTokens] = useState(() => {
    try {
      const stored = localStorage.getItem("authTokens");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Error parsing stored authTokens:", e);
      return null;
    }
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const logoutHandler = useCallback(
    async (redirectTo = t("urls.landing"), params = null) => {
      AuthLogger.logout(params?.reason || "manual");
      localStorage.removeItem("user");
      localStorage.removeItem("authTokens");

      const cookieRemovalPromises = Object.keys(Cookies.get()).map(
        (cookieName) => {
          return new Promise((resolve) => {
            if (cookieName.startsWith("intercom")) {
              Cookies.remove(cookieName, { path: "/" });
              Cookies.remove(cookieName, {
                path: "/",
                domain: window.location.hostname,
              });
              Cookies.remove(cookieName, {
                path: "/",
                domain: `.${window.location.hostname}`,
              });
            }
            resolve();
          });
        }
      );

      // Wait for all cookie removals to complete
      await Promise.all(cookieRemovalPromises);

      // Shutdown and reboot Intercom without user credentials.
      // Skipped after the end of the pilot, where Intercom is never booted
      if (!when(END_OF_PILOT_25_ARGS)) {
        const intercomCleanup = new Promise((resolve) => {
          Intercom("shutdown");
          Intercom("boot", {
            app_id: "ged8p9l8",
            custom_launcher_selector: ".my_custom_launcher",
          });
          resolve();
        });

        await intercomCleanup;
      }

      const redirectUrl = new URL(redirectTo, window.location.origin);

      if (params) {
        // If params object is provided, use its key-value pairs as query parameters
        const newParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          newParams.set(key, value);
        });
        redirectUrl.search = newParams.toString();
      } else {
        // If no params object is provided, use the existing location search params
        redirectUrl.search = window.location.search;
      }

      window.location.href = redirectUrl.toString();
    },
    [t]
  );

  const accessRefreshSuccess = (data) => {
    const { access_token, refresh_token, user } = data;

    if (user) {
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          country: user.country?.toLowerCase(),
          ...(user.id && { id: user.id }),
        })
      );
      setUser({ ...user, ...(user.id && { id: user.id }) });
    }

    localStorage.setItem(
      "authTokens",
      JSON.stringify({ access_token, refresh_token })
    );
    setAuthTokens({ access_token, refresh_token });

    // Reset the refresh interval with new token expiration
    if (interval.current) {
      clearInterval(interval.current);
      // The useEffect will set up a new interval
    }
  };

  const { mutate: refreshMutation } = useMutation(
    async ({ refresh_token, username }) => {
      // Log the refresh token details for debugging
      console.log("Refresh mutation called with:", {
        hasRefreshToken: !!refresh_token,
        refreshTokenType: typeof refresh_token,
        username,
      });

      // Validate refresh token before attempting refresh
      const validation = validateRefreshToken(refresh_token);
      if (!validation.valid) {
        AuthLogger.log(AuthLogger.WARN, "REFRESH_TOKEN_VALIDATION_WARNING", {
          reason: validation.reason,
          refreshToken: refresh_token ? "exists" : "missing",
        });
        // Don't throw error here - let the server validate the token
        // Client-side validation might be wrong about token format
      }

      setIsRefreshing(true);
      AuthLogger.tokenRefreshStarted();
      const response = await fetch(`${REACT_APP_API}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "x-client-id": process.env.REACT_APP_CLIENT_ID || "lego-test",
        },
        body: JSON.stringify({ refreshToken: refresh_token }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Token refresh failed: ${errorText}`);
        const error = new Error(errorText);
        error.status = response.status;
        throw error;
      }

      // Check if response has content before trying to parse as JSON
      const responseText = await response.text();
      if (!responseText.trim()) {
        const error = new Error(
          "Server returned empty response for token refresh"
        );
        error.status = response.status;
        throw error;
      }

      try {
        const data = JSON.parse(responseText);
        // Transform TES auth response to match expected format
        return {
          access_token: data.accessToken,
          refresh_token: data.refreshToken,
          user: data.user,
        };
      } catch (parseError) {
        console.error(
          "Failed to parse token refresh response as JSON:",
          responseText
        );
        const error = new Error(
          `Invalid JSON response from token refresh endpoint: ${parseError.message}`
        );
        error.status = response.status;
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        accessRefreshSuccess(data);
        setIsRefreshing(false);
        AuthLogger.tokenRefreshSuccess();
      },
      onError: (error, variables, context) => {
        setIsRefreshing(false);
        AuthLogger.tokenRefreshFailed(error, context?.retryCount || 0);

        console.error("Token refresh error:", {
          message: error.message,
          status: error.status,
          retryCount: context?.retryCount || 0,
        });

        // Check if this is a validation error from our own code
        const isValidationError =
          error.message === "Missing or invalid refresh token" ||
          error.message === "Empty refresh token";

        // Check if this is a JSON parsing error (server returned non-JSON response)
        const isParsingError =
          error.message.includes("Invalid JSON response") ||
          error.message.includes("Server returned empty response") ||
          error.message.includes("Unexpected end of JSON input");

        // Only logout on authentication failures, not network errors, validation errors, or parsing errors
        const isAuthError = error.status === 401 || error.status === 403;
        const isNetworkError = !error.status || error.status === 0;

        if (isAuthError && !isValidationError && !isParsingError) {
          // Only logout if it's a real auth error from the server
          AuthLogger.log(AuthLogger.ERROR, "AUTH_ERROR_LOGOUT", {
            status: error.status,
            message: error.message,
          });

          // Auth failed during refresh, redirect to login with current URL as return parameter
          const currentUrl = `${window.location.pathname}${window.location.search}`;
          const loginUrl = `${t("urls.login")}?redirectTo=${encodeURIComponent(currentUrl)}`;
          logoutHandler(loginUrl, { reason: "auth_failed" });
        } else if (isValidationError) {
          // For validation errors, log but don't logout - the token might be corrupted
          AuthLogger.log(AuthLogger.ERROR, "REFRESH_TOKEN_VALIDATION_ERROR", {
            message: error.message,
          });
          // Don't logout here - let the user continue working with their access token
        } else if (isNetworkError) {
          AuthLogger.networkError("token_refresh", error);
        } else {
          AuthLogger.log(AuthLogger.WARN, "REFRESH_ERROR_RETRY", {
            status: error.status,
          });
        }
      },
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error.status === 401 || error.status === 403) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s
        return Math.min(1000 * Math.pow(2, attemptIndex), 4000);
      },
    }
  );

  const { mutate: loginMutation, error: loginError } = useMutation(
    async ({ username, password }) => {
      AuthLogger.loginAttempt(username);
      const response = await fetch(`${REACT_APP_API}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "x-client-id": process.env.REACT_APP_CLIENT_ID || "lego-test",
        },
        body: JSON.stringify({ email: username, password }),
      });

      if (!response.ok) {
        const errorText = await response.text();

        let errorMessage = t("login.password_alarm");

        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error || errorData.message) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (parseError) {
          // If we can't parse as JSON, use the default error message
          console.log("Could not parse error response as JSON:", parseError);
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      // Transform TES auth response to match expected format
      return {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        user: data.user,
      };
    },
    {
      onSuccess: (data) => {
        accessRefreshSuccess(data);
        AuthLogger.loginSuccess(data.user);
      },
      onError: (error) => {
        AuthLogger.loginFailed(error);
      },
    }
  );

  const { mutate: signupMutation, error: signupErrorObj } = useMutation({
    mutationFn: async ({
      firstName,
      lastName,
      email,
      password,
      addressLine1,
      addressLine2,
      addressCity,
      addressCountry,
      addressZip,
      addressState,
      takeback,
      isSubscribedToSurvey,
      isSubscribedToReminders,
      redirectTo,
    }) => {
      const response = await fetch(`${REACT_APP_API}/auth/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
          addressLine1,
          addressLine2,
          addressCity,
          addressCountry,
          addressZip,
          addressState,
          takeback,
          isSubscribedToSurvey,
          isSubscribedToReminders,
          redirectTo: redirectTo || `${window.location.origin}/login`,
        }),
      });

      if (!response.ok) {
        if ([409, 400].includes(response.status)) {
          throw new Error(response.status);
        }
        throw new Error(`Signup failed: ${await response.text()}`);
      }

      return await response.json();
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const signupError = signupErrorObj?.message || null;

  const { mutate: resendEmailMutation, error: resendEmailError } = useMutation(
    async ({ email, redirectTo }) => {
      const response = await fetch(`${REACT_APP_API}/auth/resend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({ email, redirectTo }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to resend verification email: ${errorText}`);
      }

      return response.json();
    },
    {
      onError: (error) => {
        console.error(error);
      },
    }
  );

  const loginHandler = (
    { username, password },
    { onSuccess = () => null } = {},
    { onError = () => null } = {}
  ) => {
    return loginMutation(
      { username, password },
      {
        onSuccess: (data) => {
          accessRefreshSuccess(data);
          onSuccess(data);
        },
        onError: (error) => {
          console.error(error);
          onError(error);
        },
      }
    );
  };

  const signupHandler = (
    payload,
    { onSuccess = () => null, onError = () => null } = {}
  ) => {
    return signupMutation(payload, {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (error) => {
        console.error(error);
        onError(error);
      },
    });
  };

  const resendEmailVerificationHandler = (
    { email, redirectTo },
    { onSuccess = () => null } = {},
    { onError = () => null } = {}
  ) => {
    return resendEmailMutation(
      { email, redirectTo },
      {
        onSuccess: (data) => {
          onSuccess(data);
        },
        onError: (error) => {
          console.error(error);
          onError(error);
        },
      }
    );
  };

  const interval = useRef();

  // Token expiration check on app load/refresh
  useEffect(() => {
    const checkTokenExpiration = () => {
      if (authTokens?.access_token && user?.email) {
        const accessToken = authTokens.access_token;

        // Check if access token is expired (without buffer time for immediate check)
        if (isTokenExpired(accessToken, 0)) {
          console.log("Access token expired on app load, logging out user");
          AuthLogger.log(AuthLogger.WARN, "TOKEN_EXPIRED_ON_LOAD", {
            userId: user.id,
            email: user.email,
          });

          // Token is expired, redirect to login with current URL as return parameter
          const currentUrl = `${window.location.pathname}${window.location.search}`;
          const loginUrl = `${t("urls.login")}?redirectTo=${encodeURIComponent(currentUrl)}`;
          logoutHandler(loginUrl, { reason: "token_expired_on_load" });
          return;
        }

        console.log("Access token is still valid on app load");
      }
    };

    // Only check on initial load, not on every state change
    if (isInitialLoad && authTokens && user) {
      checkTokenExpiration();
    }
  }, [authTokens, user, isInitialLoad, logoutHandler, t]);

  // Initial token refresh on load
  useEffect(() => {
    if (authTokens?.refresh_token && user?.email && isInitialLoad) {
      console.log("Initial token refresh on load:", {
        hasRefreshToken: !!authTokens.refresh_token,
        hasEmail: !!user.email,
        refreshTokenPreview: authTokens.refresh_token
          ? `${authTokens.refresh_token.substring(0, 20)}...`
          : "N/A",
      });

      // Check if access token is expired before attempting refresh
      if (
        authTokens.access_token &&
        isTokenExpired(authTokens.access_token, 0)
      ) {
        console.log("Access token expired, attempting refresh on load");
        refreshMutation({
          refresh_token: authTokens.refresh_token,
          username: user.email,
        });
      } else {
        console.log("Access token valid, skipping initial refresh on load");
      }

      setIsInitialLoad(false);
    }
  }, [authTokens, refreshMutation, user, isInitialLoad]);

  // Set up refresh interval - separate effect to prevent resetting
  useEffect(() => {
    // Clear any existing interval
    if (interval.current) {
      clearInterval(interval.current);
    }

    // Only set up interval if we have valid auth
    const hasAuth = authTokens?.refresh_token && user?.email;

    if (hasAuth) {
      // Calculate refresh interval based on token expiration
      const setupRefreshInterval = () => {
        let currentTokens;
        try {
          const storedTokens = localStorage.getItem("authTokens");
          currentTokens = storedTokens ? JSON.parse(storedTokens) : null;
        } catch (e) {
          console.error("Error parsing authTokens in setupRefreshInterval:", e);
          currentTokens = null;
        }

        const tokenExpiration = getTokenExpiration(currentTokens?.access_token);

        let refreshInterval = 4 * 60 * 1000; // Default to 4 minutes

        if (tokenExpiration) {
          const now = Date.now();
          const timeUntilExpiry = tokenExpiration - now;

          // Refresh at 80% of token lifetime, with minimum of 5 minutes
          // This ensures we have plenty of time for retries if needed
          refreshInterval = Math.max(5 * 60 * 1000, timeUntilExpiry * 0.8);

          console.log(
            `Token expires in ${Math.round(timeUntilExpiry / 1000)}s, will refresh in ${Math.round(refreshInterval / 1000)}s`
          );
        }

        interval.current = setInterval(() => {
          // Get fresh values from localStorage to avoid stale closures
          let currentTokens, currentUser;
          try {
            const storedTokens = localStorage.getItem("authTokens");
            currentTokens = storedTokens ? JSON.parse(storedTokens) : null;
          } catch (e) {
            console.error("Error parsing authTokens in interval:", e);
            currentTokens = null;
          }

          try {
            const storedUser = localStorage.getItem("user");
            currentUser = storedUser ? JSON.parse(storedUser) : null;
          } catch (e) {
            console.error("Error parsing user in interval:", e);
            currentUser = null;
          }

          if (currentTokens?.refresh_token && currentUser?.email) {
            console.log("Interval refresh check:", {
              hasRefreshToken: !!currentTokens.refresh_token,
              hasEmail: !!currentUser.email,
              refreshTokenPreview: currentTokens.refresh_token
                ? `${currentTokens.refresh_token.substring(0, 20)}...`
                : "N/A",
            });

            // Let the server validate the refresh token
            refreshMutation({
              refresh_token: currentTokens.refresh_token,
              username: currentUser.email,
            });
          } else {
            console.warn("Skipping refresh - no valid tokens/user", {
              hasTokens: !!currentTokens,
              hasRefreshToken: !!currentTokens?.refresh_token,
              hasUser: !!currentUser,
              hasEmail: !!currentUser?.email,
            });
          }
        }, refreshInterval);
      };

      setupRefreshInterval();
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [authTokens?.refresh_token, user?.email, refreshMutation]);

  // Manual refresh function that can be called on demand
  const refreshTokens = () => {
    let currentTokens, currentUser;
    try {
      const storedTokens = localStorage.getItem("authTokens");
      currentTokens = storedTokens ? JSON.parse(storedTokens) : null;
    } catch (e) {
      console.error("Error parsing authTokens in refreshTokens:", e);
      currentTokens = null;
    }

    try {
      const storedUser = localStorage.getItem("user");
      currentUser = storedUser ? JSON.parse(storedUser) : null;
    } catch (e) {
      console.error("Error parsing user in refreshTokens:", e);
      currentUser = null;
    }

    if (currentTokens?.refresh_token && currentUser?.email) {
      return refreshMutation({
        refresh_token: currentTokens.refresh_token,
        username: currentUser.email,
      });
    }
    return Promise.reject(new Error("No valid tokens for refresh"));
  };

  // Check token expiration when user focuses window (returns to tab)
  useEffect(() => {
    const handleWindowFocus = () => {
      if (authTokens?.access_token && user?.email) {
        // Check if token expired while user was away
        if (isTokenExpired(authTokens.access_token, 0)) {
          console.log("Access token expired while away, logging out user");
          AuthLogger.log(AuthLogger.WARN, "TOKEN_EXPIRED_WHILE_AWAY", {
            userId: user.id,
            email: user.email,
          });

          // Token is expired, redirect to login with current URL as return parameter
          const currentUrl = `${window.location.pathname}${window.location.search}`;
          const loginUrl = `${t("urls.login")}?redirectTo=${encodeURIComponent(currentUrl)}`;
          logoutHandler(loginUrl, { reason: "token_expired_while_away" });
        }
      }
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [authTokens, user, logoutHandler, t]);

  return (
    <AuthContext.Provider
      value={{
        user,
        authTokens,
        logoutHandler,
        loginHandler,
        loginError,
        signupHandler,
        signupError,
        resendEmailVerificationHandler,
        resendEmailError,
        refreshMutation,
        refreshTokens,
        isRefreshing,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
